import { Link } from 'react-router-dom';

function Layer({ title, desc, items }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-[var(--text-primary)]">{title}</div>
          <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{desc}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-semibold text-[var(--text-secondary)]">
          Layer
        </div>
      </div>
      <div className="mt-4 font-mono text-xs leading-6 text-[var(--text-secondary)]">{items}</div>
    </div>
  );
}

export default function Architecture() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.18),transparent_45%),radial-gradient(circle_at_70%_35%,rgba(6,182,212,0.10),transparent_55%),radial-gradient(circle_at_60%_80%,rgba(212,165,116,0.10),transparent_60%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Architecture
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Identity Management Architecture
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            End-to-end identity fabric from source to trust decision, designed for cloud, on-prem, hybrid, and sovereign
            environments.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request Architecture Workshop
            </Link>
            <Link
              to="/why-identity-matters"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Why Identity Matters
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Layered identity fabric
          </div>
          <div className="mt-6 grid gap-4">
            <Layer
              title="Layer 7: Compliance & Audit"
              desc="Evidence-ready reporting, continuous access reviews, and audit automation."
              items="Regulatory reporting • Evidence collection • Access reviews"
            />
            <Layer
              title="Layer 6: ITDR & Analytics"
              desc="Threat detection, behavioral analytics, and response across identity control points."
              items="Threat detection • Behavioral analytics • Response"
            />
            <Layer
              title="Layer 5: PKI, Certificates & Keys"
              desc="Cryptographic trust: certificate lifecycle, key management, and HSM-backed assurance."
              items="Certificate authority • Key management • HSMs"
            />
            <Layer
              title="Layer 4: Privileged Access"
              desc="Least privilege, session controls, credential vaulting, and JIT access."
              items="Credential vaults • Session monitoring • JIT access"
            />
            <Layer
              title="Layer 3: Authentication"
              desc="Phishing-resistant MFA, passwordless, biometrics, and risk-based controls."
              items="MFA • Passwordless • Biometrics • Risk-based"
            />
            <Layer
              title="Layer 2: Identity Governance"
              desc="Lifecycle automation, access requests, certifications, SoD, and policies."
              items="Lifecycle • Access requests • Certifications • Policies"
            />
            <Layer
              title="Layer 1: Identity Sources"
              desc="Authoritative systems and directories feeding the identity fabric."
              items="HR • AD • LDAP • Cloud IdPs • SaaS apps • APIs • DB"
            />
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-8 backdrop-blur-xl">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Architecture principles
          </div>
          <div className="mt-5 grid gap-3">
            {[
              ["Zero Trust Foundation", "Never trust, always verify"],
              ["Least Privilege", "Minimum necessary access"],
              ["Defense in Depth", "Multiple security layers"],
              ["Continuous Verification", "Every access, every time"],
              ["Identity-Centric", "Identity as the control plane"],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="text-sm font-semibold text-[var(--text-primary)]">{t}</div>
                <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
